import React from "react";
import { Link } from "react-router-dom";
import daydreamLogo from "./ddf_logo.png";
import addupLogo from "./addup_logo.png";
import kanaLogo from "./kana_mascot_white_inside.png";
import lunaLogo from "./lunatarot_logo.png";
import stirredLogo from "./stirred_logo.png";
import sueoLogo from "./dailysign_logo.png";
import { trackStoreClick } from "./firebaseAnalytics";
import "./App.css";

// 소셜 프로필(틱톡·X·인스타그램)의 "링크 인 바이오"용 짧은 페이지.
// 첫 화면(App.tsx)과 같은 스토어 주소를 쓰고, 클릭은 source_page "links" 로 집계한다.
// 언어 목록·감지·저장 키는 첫 화면과 같아서, 한쪽에서 고른 언어가 다른 쪽에도 적용된다.

type Locale = "KO" | "EN" | "JA" | "ES" | "FR" | "PT" | "VI";
type StoreApp = "kanatomo" | "lunatarot" | "addup" | "stirred";

const LOCALE_STORAGE_KEY = "ddf-home-locale";
const locales: Locale[] = ["KO", "EN", "JA", "ES", "FR", "PT", "VI"];

const detectLocale = (): Locale => {
  if (typeof window === "undefined") return "KO";
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && locales.includes(stored as Locale)) return stored as Locale;
  } catch {
    // 저장소를 못 읽으면 브라우저 언어로 정한다.
  }
  const browserLocales = window.navigator.languages?.length
    ? window.navigator.languages
    : [window.navigator.language];
  for (const locale of browserLocales) {
    const normalized = (locale || "").toLowerCase();
    if (normalized.startsWith("ko")) return "KO";
    if (normalized.startsWith("ja")) return "JA";
    if (normalized.startsWith("es")) return "ES";
    if (normalized.startsWith("fr")) return "FR";
    if (normalized.startsWith("pt")) return "PT";
    if (normalized.startsWith("vi")) return "VI";
    if (normalized.startsWith("en")) return "EN";
  }
  return "EN";
};

type AppEntry = {
  id: StoreApp | "dailysign";
  name: string | Record<Locale, string>;
  logo: string;
  accent: string;
  tagline: Record<Locale, string>;
  appStore?: string;
  googlePlay?: string;
  comingSoon?: boolean;
};

const apps: AppEntry[] = [
  {
    id: "stirred",
    name: "Stirred",
    logo: stirredLogo,
    accent: "var(--peach)",
    tagline: {
      KO: "만들며 익히는 칵테일 가이드",
      EN: "Learn cocktails by making them",
      JA: "作りながら覚えるカクテルガイド",
      ES: "Aprende cócteles preparándolos",
      FR: "Apprenez les cocktails en les préparant",
      PT: "Aprenda coquetéis preparando",
      VI: "Học pha cocktail bằng cách tự pha",
    },
    appStore: "https://apps.apple.com/app/id6792024242",
    googlePlay: "https://play.google.com/store/apps/details?id=net.daydreamfactory.stirred",
  },
  {
    id: "addup",
    name: "Add Up",
    logo: addupLogo,
    accent: "var(--blue)",
    tagline: {
      KO: "할 일 · 루틴 · 목표 로드맵",
      EN: "To-dos, routines, goal roadmaps",
      JA: "ToDo・ルーティン・目標ロードマップ",
      ES: "Tareas, rutinas y metas por pasos",
      FR: "Tâches, routines et feuilles de route",
      PT: "Tarefas, rotinas e metas por etapas",
      VI: "Việc cần làm, thói quen, lộ trình mục tiêu",
    },
    appStore: "https://apps.apple.com/app/id6776875043",
  },
  {
    id: "kanatomo",
    name: "KanaTomo",
    logo: kanaLogo,
    accent: "var(--kana)",
    tagline: {
      KO: "예문으로 익히는 일본어",
      EN: "Japanese vocabulary in context",
      JA: "例文で覚える日本語",
      ES: "Japonés a través de ejemplos",
      FR: "Le japonais par les exemples",
      PT: "Japonês por frases de exemplo",
      VI: "Học tiếng Nhật qua câu ví dụ",
    },
    appStore: "https://apps.apple.com/kr/app/kanatomo/id6749524613",
    googlePlay: "https://play.google.com/store/apps/details?id=net.daydreamfactory.kanatomo",
  },
  {
    id: "lunatarot",
    name: "LunaTarot",
    logo: lunaLogo,
    accent: "var(--luna)",
    tagline: {
      KO: "감정 기록 타로 저널",
      EN: "A tarot journal for your feelings",
      JA: "気持ちを記録するタロットジャーナル",
      ES: "Un diario de tarot para tus emociones",
      FR: "Un journal de tarot pour vos émotions",
      PT: "Um diário de tarô para suas emoções",
      VI: "Nhật ký tarot ghi lại cảm xúc",
    },
    appStore: "https://apps.apple.com/app/id6755215945",
    googlePlay: "https://play.google.com/store/apps/details?id=net.daydreamfactory.lunatarot",
  },
  {
    id: "dailysign",
    // 첫 화면처럼 한국어에서만 "매일수어", 나머지 언어는 "DailySign".
    name: { KO: "매일수어", EN: "DailySign", JA: "DailySign", ES: "DailySign", FR: "DailySign", PT: "DailySign", VI: "DailySign" },
    logo: sueoLogo,
    accent: "var(--mint)",
    tagline: {
      KO: "하루 5단어, 한국수어 학습",
      EN: "Korean Sign Language, five words a day",
      JA: "1日5単語の韓国手話学習",
      ES: "Lengua de señas coreana, 5 palabras al día",
      FR: "Langue des signes coréenne, 5 mots par jour",
      PT: "Língua de sinais coreana, 5 palavras por dia",
      VI: "Ngôn ngữ ký hiệu Hàn Quốc, 5 từ mỗi ngày",
    },
    comingSoon: true,
  },
];

const webServices: Array<{ name: string; tagline: Record<Locale, string>; url: string; accent: string }> = [
  {
    name: "Luna",
    tagline: {
      KO: "별자리 운세 · 꿈해몽 · 타로 사전",
      EN: "Horoscopes · Dreams · Tarot",
      JA: "星占い・夢占い・タロット辞典",
      ES: "Horóscopo · Sueños · Tarot",
      FR: "Horoscope · Rêves · Tarot",
      PT: "Horóscopo · Sonhos · Tarô",
      VI: "Tử vi · Giải mộng · Tarot",
    },
    url: "https://luna.daydreamfactory.net",
    accent: "var(--lav)",
  },
  {
    name: "보이즈앤걸즈",
    tagline: {
      KO: "3분 심리테스트",
      EN: "3-minute psychology tests (Korean)",
      JA: "3分の心理テスト（韓国語）",
      ES: "Tests de personalidad de 3 min (en coreano)",
      FR: "Tests psychologiques de 3 min (en coréen)",
      PT: "Testes psicológicos de 3 min (em coreano)",
      VI: "Trắc nghiệm tâm lý 3 phút (tiếng Hàn)",
    },
    url: "https://boysngirls.co.kr",
    accent: "var(--rose)",
  },
];

const copy: Record<Locale, { subtitle: string; apps: string; web: string; soon: string; home: string; appStore: string; googlePlay: string }> = {
  KO: { subtitle: "Small apps, made with care.", apps: "앱", web: "웹에서 바로", soon: "출시 준비 중", home: "스튜디오 홈 →", appStore: "App Store", googlePlay: "Google Play" },
  EN: { subtitle: "Small apps, made with care.", apps: "Apps", web: "On the web", soon: "Coming soon", home: "Studio home →", appStore: "App Store", googlePlay: "Google Play" },
  JA: { subtitle: "Small apps, made with care.", apps: "アプリ", web: "ウェブで", soon: "近日公開", home: "スタジオホーム →", appStore: "App Store", googlePlay: "Google Play" },
  ES: { subtitle: "Small apps, made with care.", apps: "Apps", web: "En la web", soon: "Próximamente", home: "Inicio del estudio →", appStore: "App Store", googlePlay: "Google Play" },
  FR: { subtitle: "Small apps, made with care.", apps: "Applis", web: "Sur le web", soon: "Bientôt disponible", home: "Accueil du studio →", appStore: "App Store", googlePlay: "Google Play" },
  PT: { subtitle: "Small apps, made with care.", apps: "Apps", web: "Na web", soon: "Em breve", home: "Página do estúdio →", appStore: "App Store", googlePlay: "Google Play" },
  VI: { subtitle: "Small apps, made with care.", apps: "Ứng dụng", web: "Trên web", soon: "Sắp ra mắt", home: "Trang chủ studio →", appStore: "App Store", googlePlay: "Google Play" },
};

const appName = (app: AppEntry, locale: Locale) => (typeof app.name === "string" ? app.name : app.name[locale]);

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: "var(--bg-soft)", color: "var(--ink)", padding: "40px 16px 56px" },
  card: { maxWidth: 480, margin: "0 auto" },
  header: { textAlign: "center", marginBottom: 28 },
  localeRow: { display: "flex", justifyContent: "center", marginBottom: 24 },
  logo: { width: 64, height: 56, objectFit: "contain" },
  brand: { margin: "12px 0 4px", fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" },
  subtitle: { margin: 0, color: "var(--ink-3)", fontSize: 14 },
  sectionLabel: { margin: "24px 4px 10px", fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-3)" },
  item: { display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", marginBottom: 10, background: "var(--bg-paper)", border: "1px solid var(--rule-soft)", borderRadius: 18, boxShadow: "0 6px 18px rgba(31, 42, 68, 0.05)" },
  icon: { width: 48, height: 48, borderRadius: 14, objectFit: "cover", flexShrink: 0, border: "1px solid var(--rule-soft)" },
  info: { flex: 1, minWidth: 0 },
  name: { margin: 0, fontSize: 16, fontWeight: 800 },
  tagline: { margin: "3px 0 0", fontSize: 13, color: "var(--ink-2)" },
  buttons: { display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" },
  button: { display: "inline-flex", alignItems: "center", gap: 7, minHeight: 38, padding: "8px 14px", borderRadius: 999, background: "var(--ink)", color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none" },
  dot: { width: 7, height: 7, borderRadius: "50%" },
  soon: { display: "inline-block", marginTop: 8, padding: "5px 10px", borderRadius: 999, background: "var(--bg-tint)", color: "var(--ink-2)", fontSize: 12, fontWeight: 700 },
  webLink: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 16px", marginBottom: 10, background: "var(--bg-paper)", border: "1px solid var(--rule-soft)", borderRadius: 18, color: "var(--ink)", textDecoration: "none" },
  footer: { textAlign: "center", marginTop: 28, fontSize: 13 },
  footerLink: { color: "var(--ink-2)", textDecoration: "none", fontWeight: 600 },
};

const Links: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("KO");

  React.useEffect(() => {
    setLocale(detectLocale());
    document.title = "Daydream Factory | Links";
  }, []);

  const chooseLocale = (next: Locale) => {
    setLocale(next);
    try {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // 저장 못 해도 이번 화면에는 적용된다.
    }
  };

  React.useEffect(() => {
    document.documentElement.lang = locale.toLowerCase();
  }, [locale]);

  const t = copy[locale];
  const handleStoreClick = (appName: StoreApp, storeName: "app_store" | "google_play") => () => {
    trackStoreClick({ app_name: appName, store_name: storeName, source_page: "links" });
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.localeRow}>
          <div className="ddf-locale-pills" aria-label="Locales">
            {locales.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => chooseLocale(item)}
                className={`ddf-locale-pill${locale === item ? " is-active" : ""}`}
                aria-pressed={locale === item}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <header style={styles.header}>
          <img src={daydreamLogo} alt="Daydream Factory" style={styles.logo} />
          <h1 style={styles.brand}>Daydream Factory</h1>
          <p style={styles.subtitle}>{t.subtitle}</p>
        </header>

        <p style={styles.sectionLabel}>{t.apps}</p>
        {apps.map((app) => (
          <div key={app.id} style={styles.item}>
            <img src={app.logo} alt={appName(app, locale)} style={styles.icon} />
            <div style={styles.info}>
              <p style={styles.name}>{appName(app, locale)}</p>
              <p style={styles.tagline}>{app.tagline[locale]}</p>
              {app.comingSoon ? (
                <span style={styles.soon}>✦ {t.soon}</span>
              ) : (
                <div style={styles.buttons}>
                  {app.appStore && (
                    <a href={app.appStore} target="_blank" rel="noreferrer" style={styles.button}
                       onClick={handleStoreClick(app.id as StoreApp, "app_store")}>
                      <span style={{ ...styles.dot, background: app.accent }} />
                      {t.appStore}
                    </a>
                  )}
                  {app.googlePlay && (
                    <a href={app.googlePlay} target="_blank" rel="noreferrer" style={styles.button}
                       onClick={handleStoreClick(app.id as StoreApp, "google_play")}>
                      <span style={{ ...styles.dot, background: app.accent }} />
                      {t.googlePlay}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        <p style={styles.sectionLabel}>{t.web}</p>
        {webServices.map((service) => (
          <a key={service.name} href={service.url} target="_blank" rel="noreferrer" style={styles.webLink}>
            <span>
              <span style={{ display: "block", fontSize: 15, fontWeight: 800 }}>{service.name}</span>
              <span style={{ display: "block", fontSize: 13, color: "var(--ink-2)", marginTop: 3 }}>{service.tagline[locale]}</span>
            </span>
            <span style={{ color: service.accent, fontWeight: 800 }}>↗</span>
          </a>
        ))}

        <p style={styles.footer}>
          <Link to="/" style={styles.footerLink}>{t.home}</Link>
        </p>
      </div>
    </div>
  );
};

export default Links;
